import { ReactNode } from "react";

export interface IAdminNavLink {
  title: string;
  link: string;
  /** Routes this item is active on (defaults to its own path and below) */
  match?: RegExp;
}

export interface IAdminNavGroup {
  title: string;
  icon: ReactNode;
  /** A group without children is a plain link */
  link?: string;
  children: IAdminNavLink[];
}

// Line icons from the admin design (24×24, stroked)
const icon = (paths: ReactNode) => paths;

export const ADMIN_NAV: IAdminNavGroup[] = [
  {
    title: "Dashboard",
    link: "/admin/dashboard",
    icon: icon(
      <>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="4" rx="1.5" />
        <rect x="14" y="10" width="7" height="11" rx="1.5" />
        <rect x="3" y="13" width="7" height="8" rx="1.5" />
      </>
    ),
    children: [],
  },
  {
    title: "Configs",
    icon: icon(
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
      </>
    ),
    children: [
      { title: "Store Setup", link: "/admin/config/store-setup" },
      { title: "Workflow", link: "/admin/config/workflow" },
      {
        title: "Stores",
        link: "/admin/config/merchants/presto/stores?merchantId=1",
        match: /^\/admin\/config\/merchants\/[^/]+\/stores\/?$/,
      },
      {
        title: "Merchants",
        link: "/admin/config/merchants",
        match: /^\/admin\/config\/merchants(\/create)?\/?$/,
      },
      {
        title: "Device Access",
        link: "/admin/config/merchants/presto/stores/1/points",
        match: /^\/admin\/config\/merchants\/[^/]+\/stores\/[^/]+\/points/,
      },
      { title: "Station Management", link: "/admin/station-management" },
      { title: "Opened Stations", link: "/admin/opened-stations" },
      {
        title: "PinTag Config",
        link: "/admin/config/merchants/presto/stores/1/pin-tag",
        match: /^\/admin\/config\/merchants\/[^/]+\/stores\/[^/]+\/pin-tag/,
      },
      { title: "Racks", link: "/admin/racks" },
      { title: "Box Config", link: "/admin/boxConfig" },
    ],
  },
  {
    title: "Products",
    icon: icon(
      <>
        <path d="M21 8l-9-5-9 5 9 5 9-5z" />
        <path d="M3 8v8l9 5 9-5V8" />
      </>
    ),
    children: [
      { title: "Price Lists", link: "/admin/config/price-lists" },
      { title: "Upload Prices", link: "/admin/config/upload-price" },
      { title: "Price Setup", link: "/admin/price-setup" },
      { title: "Price Overrides", link: "/admin/price-overrides" },
      { title: "Special Charges", link: "/admin/special-charges" },
      { title: "Upcharges", link: "/admin/upcharges" },
      { title: "Releases", link: "/admin/releases" },
      { title: "Spots", link: "/admin/spots" },
      { title: "Damages", link: "/admin/damages" },
      { title: "Instructions", link: "/admin/instructions" },
    ],
  },
  {
    title: "Discounts",
    icon: icon(
      <>
        <path d="M19 5L5 19" />
        <circle cx="7" cy="7" r="2" />
        <circle cx="17" cy="17" r="2" />
      </>
    ),
    children: [{ title: "Discounts", link: "/admin/discounts" }],
  },
  {
    title: "Users",
    icon: icon(
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.8" />
      </>
    ),
    children: [
      { title: "User Management", link: "/admin/user-management" },
      { title: "Role Management", link: "/admin/role-management" },
      { title: "Permission Management", link: "/admin/permission-management" },
      { title: "Manager Access Logs", link: "/admin/manager-access-logs" },
    ],
  },
  {
    title: "Others",
    icon: icon(
      <>
        <circle cx="5" cy="12" r="1.5" />
        <circle cx="12" cy="12" r="1.5" />
        <circle cx="19" cy="12" r="1.5" />
      </>
    ),
    children: [
      { title: "Redo Reasons", link: "/admin/redo-reasons" },
      { title: "Cash Drawer Reasons", link: "/admin/cash-drawer-reasons" },
    ],
  },
];

// Admin pages that aren't in the menu but still need a header title
const UNLISTED_TITLES: IAdminNavLink[] = [
  { title: "Cashier Points", link: "/admin/cashier-points" },
];

const pathOf = (link: string) => link.split("?")[0];

const matches = (item: IAdminNavLink, pathname: string) =>
  item.match
    ? item.match.test(pathname)
    : pathname === pathOf(item.link) ||
      pathname.startsWith(`${pathOf(item.link)}/`);

/** The menu entry for a route: the group and, inside it, the page. */
export const findAdminNavItem = (
  pathname: string
): { group?: IAdminNavGroup; item?: IAdminNavLink } => {
  for (const group of ADMIN_NAV) {
    if (group.link && matches({ title: group.title, link: group.link }, pathname)) {
      return { group, item: { title: group.title, link: group.link } };
    }
    const item = group.children.find((child) => matches(child, pathname));
    if (item) return { group, item };
  }
  const unlisted = UNLISTED_TITLES.find((child) => matches(child, pathname));
  return { item: unlisted };
};
